/**
 * AuditLogsPage - Pagina Log di Audit per Super Admin
 * Visualizza tutte le azioni degli utenti con filtri avanzati
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, Filter, Download, Trash2, RefreshCw, 
  ChevronLeft, ChevronRight, Calendar, User, 
  Activity, Clock, CheckCircle, XCircle, AlertTriangle,
  FileText, Shield, Database, Eye, X, Globe, Monitor,
  Minimize2, Maximize2, Copy, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Mappatura categorie -> icone e colori
const categoryConfig: Record<string, { icon: any; color: string; label: string }> = {
  auth: { icon: Shield, color: 'bg-blue-100 text-blue-700', label: 'Autenticazione' },
  user: { icon: User, color: 'bg-purple-100 text-purple-700', label: 'Utenti' },
  order: { icon: FileText, color: 'bg-green-100 text-green-700', label: 'Ordini' },
  product: { icon: Database, color: 'bg-yellow-100 text-yellow-700', label: 'Prodotti' },
  catalog: { icon: Database, color: 'bg-lime-100 text-lime-700', label: 'Catalogo' },
  pricelist: { icon: FileText, color: 'bg-violet-100 text-violet-700', label: 'Listini Prezzi' },
  stock: { icon: Database, color: 'bg-amber-100 text-amber-700', label: 'Giacenze' },
  relationship: { icon: User, color: 'bg-pink-100 text-pink-700', label: 'Relazioni Commerciali' },
  message: { icon: FileText, color: 'bg-indigo-100 text-indigo-700', label: 'Messaggi' },
  notification: { icon: Activity, color: 'bg-orange-100 text-orange-700', label: 'Notifiche' },
  association: { icon: User, color: 'bg-teal-100 text-teal-700', label: 'Associazioni' },
  groupage: { icon: Database, color: 'bg-cyan-100 text-cyan-700', label: 'Groupage' },
  company: { icon: User, color: 'bg-sky-100 text-sky-700', label: 'Azienda' },
  delivery: { icon: Activity, color: 'bg-rose-100 text-rose-700', label: 'Consegne' },
  logistics: { icon: Activity, color: 'bg-fuchsia-100 text-fuchsia-700', label: 'Logistica' },
  billing: { icon: FileText, color: 'bg-emerald-100 text-emerald-700', label: 'Fatturazione' },
  payment: { icon: Activity, color: 'bg-green-100 text-green-700', label: 'Pagamenti' },
  favorite: { icon: Activity, color: 'bg-red-100 text-red-700', label: 'Preferiti' },
  team: { icon: User, color: 'bg-blue-100 text-blue-700', label: 'Team' },
  analytics: { icon: Activity, color: 'bg-purple-100 text-purple-700', label: 'Statistiche' },
  settings: { icon: Activity, color: 'bg-gray-100 text-gray-700', label: 'Impostazioni' },
  gdpr: { icon: Shield, color: 'bg-slate-100 text-slate-700', label: 'GDPR/Privacy' },
  vat: { icon: Search, color: 'bg-zinc-100 text-zinc-700', label: 'Verifica P.IVA' },
  ai: { icon: Activity, color: 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700', label: 'AI' },
  extension: { icon: Activity, color: 'bg-orange-100 text-orange-700', label: 'Estensioni' },
  admin: { icon: Shield, color: 'bg-red-100 text-red-700', label: 'Admin' },
  document: { icon: FileText, color: 'bg-amber-100 text-amber-700', label: 'Documenti' },
  other: { icon: Activity, color: 'bg-gray-100 text-gray-700', label: 'Altro' },
};

// Mappatura tipi azione -> etichette italiane
const actionTypeLabels: Record<string, string> = {
  // Auth
  login: 'Login',
  logout: 'Logout',
  login_failed: 'Login Fallito',
  password_change: 'Cambio Password',
  password_reset: 'Reset Password',
  
  // CRUD generici
  create: 'Creazione',
  read: 'Visualizzazione',
  update: 'Modifica',
  delete: 'Eliminazione',
  approve: 'Approvazione',
  reject: 'Rifiuto',
  send: 'Invio',
  export: 'Esportazione',
  import: 'Importazione',
  upload: 'Caricamento',
  download: 'Download',
  
  // Ordini
  order_create: 'Creazione Ordine',
  order_confirm: 'Conferma Ordine',
  order_cancel: 'Annullamento Ordine',
  order_ship: 'Spedizione Ordine',
  order_deliver: 'Consegna Ordine',
  order_invoice: 'Fatturazione Ordine',
  order_duplicate: 'Duplica Ordine',
  
  // Prodotti/Catalogo
  product_create: 'Creazione Prodotto',
  product_bulk_update: 'Aggiornamento Massivo Prodotti',
  product_bulk_delete: 'Eliminazione Massiva Prodotti',
  catalog_import: 'Importazione Catalogo',
  catalog_export: 'Esportazione Catalogo',
  catalog_sync: 'Sincronizzazione Catalogo',
  
  // Listini
  pricelist_create: 'Creazione Listino',
  pricelist_assign: 'Assegnazione Listino',
  pricelist_import: 'Importazione Listino',
  pricelist_export: 'Esportazione Listino',
  
  // Stock
  stock_update: 'Aggiornamento Giacenza',
  stock_import: 'Importazione Giacenze',
  stock_export: 'Esportazione Giacenze',
  stock_bulk_update: 'Aggiornamento Massivo Giacenze',
  
  // Relazioni
  relationship_request: 'Richiesta Relazione',
  relationship_accept: 'Accettazione Relazione',
  relationship_reject: 'Rifiuto Relazione',
  relationship_remove: 'Rimozione Relazione',
  relationship_block: 'Blocco Fornitore/Cliente',
  
  // Messaggi
  message_send: 'Invio Messaggio',
  message_read: 'Lettura Messaggio',
  message_delete: 'Eliminazione Messaggio',
  
  // Notifiche
  notification_read: 'Lettura Notifica',
  notification_read_all: 'Lettura Tutte Notifiche',
  notification_settings: 'Impostazioni Notifiche',
  push_subscription: 'Sottoscrizione Push',
  
  // Associazioni
  association_join: 'Iscrizione Associazione',
  association_leave: 'Uscita Associazione',
  association_create: 'Creazione Associazione',
  association_members: 'Gestione Membri',
  membership_request: 'Richiesta Membership',
  membership_approve: 'Approvazione Membership',
  membership_reject: 'Rifiuto Membership',
  
  // Groupage
  groupage_create: 'Creazione Groupage',
  groupage_join: 'Adesione Groupage',
  groupage_leave: 'Uscita Groupage',
  groupage_order: 'Ordine Groupage',
  groupage_order_confirm: 'Conferma Ordine Groupage',
  
  // Azienda
  company_create: 'Creazione Azienda',
  company_update: 'Modifica Azienda',
  company_logo_upload: 'Caricamento Logo',
  company_document_upload: 'Caricamento Documento',
  
  // Consegne
  delivery_site_create: 'Creazione Punto Consegna',
  delivery_site_update: 'Modifica Punto Consegna',
  delivery_site_delete: 'Eliminazione Punto Consegna',
  delivery_zone_create: 'Creazione Zona Consegna',
  delivery_zone_update: 'Modifica Zona Consegna',
  
  // Logistica
  logistics_route: 'Gestione Giro Consegne',
  logistics_calendar: 'Calendario Consegne',
  
  // Fatturazione
  invoice_create: 'Creazione Fattura',
  billing_export: 'Esportazione Fatture',
  
  // Pagamenti
  payment_create: 'Creazione Pagamento',
  payment_confirm: 'Conferma Pagamento',
  payment_refund: 'Rimborso',
  
  // Preferiti
  favorite_add: 'Aggiunta Preferito',
  favorite_remove: 'Rimozione Preferito',
  
  // Team
  team_invite: 'Invito Membro Team',
  team_remove: 'Rimozione Membro Team',
  team_permissions: 'Modifica Permessi',
  
  // Analytics
  stats_view: 'Visualizzazione Statistiche',
  
  // Settings
  settings_notifications: 'Impostazioni Notifiche',
  settings_privacy: 'Impostazioni Privacy',
  settings_language: 'Cambio Lingua',
  
  // GDPR
  gdpr_export: 'Esportazione Dati GDPR',
  gdpr_delete_request: 'Richiesta Cancellazione Dati',
  gdpr_consent: 'Gestione Consensi',
  
  // Documenti
  document_accept: 'Accettazione Documento',
  
  // VAT
  vat_lookup: 'Verifica Partita IVA',
  
  // AI
  ai_suggestion: 'Suggerimento AI',
  ai_generate: 'Generazione AI',
  
  // Estensioni
  extension_install: 'Installazione Estensione',
  extension_uninstall: 'Disinstallazione Estensione',
  
  // Admin
  admin_users: 'Gestione Utenti Admin',
  admin_companies: 'Gestione Aziende Admin',
  admin_associations: 'Gestione Associazioni Admin',
  admin_payments: 'Gestione Pagamenti Admin',
  admin_errors: 'Visualizzazione Errori',
  admin_gdpr: 'Gestione GDPR Admin',
  
  // Generici
  api_call: 'Chiamata API',
  page_view: 'Visualizzazione Pagina',
  mark_read: 'Segnato come Letto',
};

// Mappatura ruoli -> etichette
const roleLabels: Record<string, string> = {
  buyer: 'Buyer',
  seller: 'Fornitore',
  admin: 'Super Admin',
};

const AuditLogsPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  
  // Filtri
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [actionType, setActionType] = useState('');
  const [userRole, setUserRole] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  
  // Dialog eliminazione
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteBeforeDate, setDeleteBeforeDate] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Pannello dettagli log (slide-out)
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  
  // Vista compatta
  const [compactView, setCompactView] = useState(false);

  // Copia negli appunti
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti');
  };

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * limit));
      params.append('limit', String(limit));
      
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (actionType) params.append('action_type', actionType);
      if (userRole) params.append('user_role', userRole);
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate + 'T23:59:59').toISOString());
      if (successFilter !== '') params.append('success', successFilter);
      
      const response = await axios.get(`${API}/api/audit-logs/list?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error('Error fetching logs:', error);
      if (error.response?.status === 403) {
        toast.error('Accesso negato: solo Super Admin');
        navigate('/dashboard');
      } else {
        toast.error('Errore nel caricamento dei log');
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, category, actionType, userRole, startDate, endDate, successFilter, navigate]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch filter options
  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API}/api/audit-logs/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilters();
  }, []);

  // Reset filtri
  const resetFilters = () => {
    setSearch('');
    setCategory('');
    setActionType('');
    setUserRole('');
    setStartDate('');
    setEndDate('');
    setSuccessFilter('');
    setPage(0);
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate + 'T23:59:59').toISOString());
      if (category) params.append('category', category);
      
      const response = await axios.get(`${API}/api/audit-logs/export?${params.toString()}`);
      
      // Converti in CSV
      const data = response.data.data;
      if (data.length === 0) {
        toast.error('Nessun dato da esportare');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Esportati ${data.length} record`);
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  // Delete old logs
  const handleDeleteOldLogs = async () => {
    if (!deleteBeforeDate) {
      toast.error('Seleziona una data');
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/api/audit-logs/cleanup`, {
        data: { before_date: new Date(deleteBeforeDate).toISOString() }
      });
      
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setDeleteBeforeDate('');
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  // Formatta timestamp
  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Preset date filters
  const setDatePreset = (preset) => {
    const now = new Date();
    let start;
    
    switch (preset) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'month':
        start = new Date(now.setMonth(now.getMonth() - 1));
        break;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setPage(0);
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Log di Audit</h1>
          <p className="text-gray-500 mt-1">Tracciamento completo di tutte le azioni utente</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Switch Vista Compatta */}
          <div className="flex items-center gap-2 mr-4">
            <Switch
              id="compact-view"
              checked={compactView}
              onCheckedChange={setCompactView}
            />
            <label htmlFor="compact-view" className="text-sm text-gray-600 cursor-pointer flex items-center gap-1">
              {compactView ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              {compactView ? 'Compatta' : 'Estesa'}
            </label>
          </div>
          
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} data-testid="delete-old-btn">
            <Trash2 className="h-4 w-4 mr-2" />
            Elimina Vecchi
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Oggi</p>
                  <p className="text-2xl font-bold">{stats.totals?.today?.toLocaleString()}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Questa Settimana</p>
                  <p className="text-2xl font-bold">{stats.totals?.this_week?.toLocaleString()}</p>
                </div>
                <Calendar className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Questo Mese</p>
                  <p className="text-2xl font-bold">{stats.totals?.this_month?.toLocaleString()}</p>
                </div>
                <Activity className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Totale</p>
                  <p className="text-2xl font-bold">{stats.totals?.all_time?.toLocaleString()}</p>
                </div>
                <Database className="h-8 w-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Fallite (7gg)</p>
                  <p className="text-2xl font-bold text-red-600">{stats.totals?.failed_this_week?.toLocaleString()}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Cerca email, nome, entità..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Category */}
            <Select value={category} onValueChange={(v) => { setCategory(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {filters?.categories?.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {categoryConfig[cat]?.label || cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Action Type */}
            <Select value={actionType} onValueChange={(v) => { setActionType(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="action-type-filter">
                <SelectValue placeholder="Tipo Azione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {filters?.action_types?.map(act => (
                  <SelectItem key={act} value={act}>
                    {actionTypeLabels[act] || act}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User Role */}
            <Select value={userRole} onValueChange={(v) => { setUserRole(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="role-filter">
                <SelectValue placeholder="Ruolo Utente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i ruoli</SelectItem>
                {filters?.user_roles?.map(role => (
                  <SelectItem key={role} value={role}>
                    {roleLabels[role] || role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            {/* Start Date */}
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              placeholder="Data inizio"
              data-testid="start-date-filter"
            />

            {/* End Date */}
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              placeholder="Data fine"
              data-testid="end-date-filter"
            />

            {/* Success Filter */}
            <Select value={successFilter} onValueChange={(v) => { setSuccessFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger data-testid="success-filter">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="true">Solo Successi</SelectItem>
                <SelectItem value="false">Solo Fallimenti</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset & Presets */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDatePreset('today')}>Oggi</Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('week')}>7 gg</Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('month')}>30 gg</Button>
              <Button variant="ghost" size="sm" onClick={resetFilters}>Reset</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content: Table + Detail Panel */}
      <div className="flex gap-4">
        {/* Logs Table */}
        <Card className={`flex-1 transition-all duration-300 ${showDetailPanel ? 'w-1/2' : 'w-full'}`}>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={compactView ? 'h-8' : ''}>
                  <TableHead className={compactView ? 'py-1 text-xs' : ''}>Timestamp</TableHead>
                  <TableHead className={compactView ? 'py-1 text-xs' : ''}>Utente</TableHead>
                  <TableHead className={compactView ? 'py-1 text-xs' : ''}>Categoria</TableHead>
                  <TableHead className={compactView ? 'py-1 text-xs' : ''}>Azione</TableHead>
                  {!compactView && <TableHead>Entità</TableHead>}
                  {!compactView && <TableHead>Endpoint</TableHead>}
                  <TableHead className={`text-center ${compactView ? 'py-1 text-xs w-12' : ''}`}>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={compactView ? 5 : 7} className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={compactView ? 5 : 7} className="text-center py-8 text-gray-500">
                      Nessun log trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log: any) => {
                    const catConfig = categoryConfig[log.category] || categoryConfig.other;
                    const CategoryIcon = catConfig.icon;
                    const isSelected = selectedLog?.id === log.id;
                    
                    return (
                      <TableRow 
                        key={log.id} 
                        data-testid={`log-row-${log.id}`}
                        className={`cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 hover:bg-blue-100 border-l-2 border-l-blue-500' 
                            : 'hover:bg-gray-50'
                        } ${compactView ? 'h-8' : ''}`}
                        onClick={() => {
                          setSelectedLog(log);
                          setShowDetailPanel(true);
                        }}
                      >
                        {/* Timestamp */}
                        <TableCell className={`font-mono ${compactView ? 'py-1 text-[10px]' : 'text-xs'}`}>
                          {compactView 
                            ? new Date(log.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            : formatTimestamp(log.timestamp)
                          }
                        </TableCell>
                        
                        {/* Utente */}
                        <TableCell className={compactView ? 'py-1' : ''}>
                          {compactView ? (
                            <span className="text-xs truncate max-w-[120px] block">
                              {log.user?.email?.split('@')[0] || '-'}
                            </span>
                          ) : (
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{log.user?.name || '-'}</span>
                              <span className="text-xs text-gray-500">{log.user?.email || '-'}</span>
                              {log.user?.role && (
                                <Badge variant="outline" className="w-fit mt-1 text-xs">
                                  {roleLabels[log.user.role] || log.user.role}
                                </Badge>
                              )}
                            </div>
                          )}
                        </TableCell>
                        
                        {/* Categoria */}
                        <TableCell className={compactView ? 'py-1' : ''}>
                          {compactView ? (
                            <Badge className={`${catConfig.color} text-[10px] px-1 py-0`}>
                              {(log.details?.category_label || catConfig.label).substring(0, 8)}
                            </Badge>
                          ) : (
                            <Badge className={`${catConfig.color} flex items-center gap-1 w-fit`}>
                              <CategoryIcon className="h-3 w-3" />
                              {log.details?.category_label || catConfig.label}
                            </Badge>
                          )}
                        </TableCell>
                        
                        {/* Azione */}
                        <TableCell className={compactView ? 'py-1' : ''}>
                          <span className={`font-medium ${compactView ? 'text-[10px]' : 'text-sm'}`}>
                            {compactView 
                              ? (log.details?.action_label || actionTypeLabels[log.action_type] || log.action_type).substring(0, 12)
                              : (log.details?.action_label || actionTypeLabels[log.action_type] || log.action_type)
                            }
                          </span>
                        </TableCell>
                        
                        {/* Entità - solo vista estesa */}
                        {!compactView && (
                          <TableCell>
                            {log.entity?.type && (
                              <div className="flex flex-col">
                                <span className="text-sm">{log.entity.type}</span>
                                {log.entity.id && (
                                  <span className="text-xs text-gray-500 font-mono">
                                    {log.entity.id.substring(0, 8)}...
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                        )}
                        
                        {/* Endpoint - solo vista estesa */}
                        {!compactView && (
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-600 font-mono">
                                {log.request?.method} {log.request?.endpoint?.substring(0, 30)}
                                {log.request?.endpoint?.length > 30 ? '...' : ''}
                              </span>
                              <span className="text-xs text-gray-400">
                                {log.request?.ip_address}
                              </span>
                            </div>
                          </TableCell>
                        )}
                        
                        {/* Stato */}
                        <TableCell className={`text-center ${compactView ? 'py-1' : ''}`}>
                          {log.success ? (
                            <CheckCircle className={`text-green-500 mx-auto ${compactView ? 'h-3 w-3' : 'h-5 w-5'}`} />
                          ) : (
                            <XCircle className={`text-red-500 mx-auto ${compactView ? 'h-3 w-3' : 'h-5 w-5'}`} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Detail Panel (Slide-out) */}
        {showDetailPanel && selectedLog && (
          <Card className="w-[500px] flex-shrink-0">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                <span className="font-semibold">Dettagli Evento</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDetailPanel(false);
                  setSelectedLog(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Panel Content */}
            <div className="p-4 space-y-4">
              {/* Status Bar */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                selectedLog.success ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <div className="flex items-center gap-2">
                  {selectedLog.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${selectedLog.success ? 'text-green-700' : 'text-red-700'}`}>
                    {selectedLog.success ? 'Successo' : 'Fallito'}
                  </span>
                  {selectedLog.details?.status_code && (
                    <Badge variant="outline" className="ml-2">
                      HTTP {selectedLog.details.status_code}
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-gray-500">{selectedLog.details?.duration_ms}ms</span>
              </div>

              {/* Timestamp */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-[10px] text-gray-400 uppercase">Data</p>
                  <p className="font-mono text-xs">{new Date(selectedLog.timestamp).toLocaleDateString('it-IT')}</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-[10px] text-gray-400 uppercase">Ora</p>
                  <p className="font-mono text-xs">{new Date(selectedLog.timestamp).toLocaleTimeString('it-IT')}</p>
                </div>
              </div>

              {/* User Section */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Utente</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-gray-400">Email</p>
                    <p className="text-sm font-medium truncate">{selectedLog.user?.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Ruolo</p>
                    <Badge variant="outline" className="text-xs">
                      {roleLabels[selectedLog.user?.role] || selectedLog.user?.role || '-'}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] text-gray-400">ID</p>
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-mono truncate flex-1">{selectedLog.user?.id || '-'}</p>
                      {selectedLog.user?.id && (
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedLog.user.id)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Section */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Azione</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-gray-400">Categoria</p>
                    <Badge className={`${(categoryConfig[selectedLog.category] || categoryConfig.other).color} text-xs`}>
                      {selectedLog.details?.category_label || categoryConfig[selectedLog.category]?.label || selectedLog.category}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Tipo</p>
                    <p className="text-sm font-medium">
                      {selectedLog.details?.action_label || actionTypeLabels[selectedLog.action_type] || selectedLog.action_type}
                    </p>
                  </div>
                </div>
              </div>

              {/* Request Section */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Richiesta</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {selectedLog.request?.method}
                    </Badge>
                    <p className="text-xs font-mono text-gray-600 truncate flex-1">{selectedLog.request?.endpoint}</p>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedLog.request?.endpoint || '')}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-gray-400">IP</p>
                      <p className="text-xs font-mono">{selectedLog.request?.ip_address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Durata</p>
                      <p className="text-xs">{selectedLog.details?.duration_ms ? `${selectedLog.details.duration_ms}ms` : '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Entity Section */}
              {(selectedLog.entity?.type || selectedLog.entity?.id) && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Entità</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-gray-400">Tipo</p>
                      <p className="text-sm">{selectedLog.entity?.type || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">ID</p>
                      <div className="flex items-center gap-1">
                        <p className="text-xs font-mono truncate">{selectedLog.entity?.id || '-'}</p>
                        {selectedLog.entity?.id && (
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedLog.entity.id)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* User Agent */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Client</span>
                </div>
                <p className="text-[10px] text-gray-500 break-all leading-relaxed">
                  {selectedLog.request?.user_agent || '-'}
                </p>
              </div>

              {/* Query Params */}
              {selectedLog.details?.query_params && Object.keys(selectedLog.details.query_params).length > 0 && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Query Params</span>
                  </div>
                  <pre className="text-[10px] bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(selectedLog.details.query_params, null, 2)}
                  </pre>
                </div>
              )}

              {/* Error Message */}
              {selectedLog.error_message && (
                <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-xs font-semibold text-red-600 uppercase">Errore</span>
                  </div>
                  <p className="text-xs text-red-600 font-mono">{selectedLog.error_message}</p>
                </div>
              )}

              {/* Event ID */}
              <div className="pt-2 border-t">
                <p className="text-[10px] text-gray-400 uppercase">ID Evento</p>
                <div className="flex items-center gap-1">
                  <p className="text-[10px] font-mono text-gray-500 truncate flex-1">{selectedLog.id}</p>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedLog.id)}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} di {total} risultati
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="h-4 w-4" />
            Precedente
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= total}
            data-testid="next-page-btn"
          >
            Successivo
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Elimina Log Vecchi
            </DialogTitle>
            <DialogDescription>
              Questa operazione eliminerà permanentemente tutti i log precedenti alla data selezionata.
              L'operazione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Elimina log precedenti a:</label>
            <Input
              type="date"
              value={deleteBeforeDate}
              onChange={(e) => setDeleteBeforeDate(e.target.value)}
              className="mt-2"
              data-testid="delete-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteOldLogs}
              disabled={!deleteBeforeDate || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Eliminazione...' : 'Elimina Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
